import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import {
  X,
  DollarSign,
  CalendarClock,
  AlertTriangle,
  HelpCircle,
  LogOut,
  CheckCircle2,
  type LucideIcon,
} from 'lucide-react'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'

export type ReimbursementProcessChoice = 'EVICTION' | 'SKIPPED' | 'NOT_NOW'

export type StartReimbursementProcessVariant = 'not_paid_only' | 'open_reimbursement'

const IN_UNIT_ACTIONS: {
  value: 'yes' | 'no'
  label: string
  description: string
  icon: LucideIcon
  testId: string
  choice: ReimbursementProcessChoice
}[] = [
  {
    value: 'yes',
    label: 'Yes, the resident is still in the unit.',
    description: 'Create an Eviction reimbursement request for nonpayment of rent.',
    icon: CheckCircle2,
    testId: 'start-reimbursement-process-option-in-unit-yes',
    choice: 'EVICTION',
  },
  {
    value: 'no',
    label: 'No, the resident has vacated the unit.',
    description: 'Create a Skipped reimbursement request for nonpayment of rent.',
    icon: LogOut,
    testId: 'start-reimbursement-process-option-in-unit-no',
    choice: 'SKIPPED',
  },
]

function ReimbursementTimelineSections() {
  return (
    <>
      <div className="rounded-2xl border border-sky-200 bg-sky-50 p-5 text-slate-700">
        <div className="mb-3 flex items-center gap-3">
          <CalendarClock className="text-sky-600" size={24} aria-hidden="true" />
          <h3 className="text-base font-bold text-slate-900">Time Sensitive Requirements</h3>
        </div>
        <ul className="ml-7 list-disc space-y-2 text-sm leading-relaxed">
          <li>
            Reimbursement requests must be opened within the same first month that rent was not
            paid
          </li>
          <li>
            Legal must be filed within the same first month that rent was not paid (30-day NTV only
            exception)
          </li>
        </ul>
      </div>

      <div
        className="flex items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-5 text-slate-900"
        data-testid="start-reimbursement-process-warning"
      >
        <AlertTriangle className="shrink-0 text-amber-600" size={24} aria-hidden="true" />
        <p className="text-base font-bold">
          Reimbursements will be reduced if timelines are not met
        </p>
      </div>
    </>
  )
}

export interface StartReimbursementProcessDialogProps {
  isOpen: boolean
  variant: StartReimbursementProcessVariant
  residentName: string
  applicantId: string | number
  onClose: () => void
  onSelect: (choice: ReimbursementProcessChoice) => void
}

/**
 * Bulk "Mark Rent Not Paid" modals from the resident list toolbar.
 * - not_paid_only: timeline + warning + Confirm/Cancel (NOT_NOW)
 * - open_reimbursement: timeline + warning + in-unit choice → immediate EVICTION/SKIPPED
 */
export function StartReimbursementProcessDialog({
  isOpen,
  variant,
  residentName,
  applicantId,
  onClose,
  onSelect,
}: StartReimbursementProcessDialogProps) {
  useBodyScrollLock(isOpen)

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const isNotPaidOnly = variant === 'not_paid_only'
  const title = isNotPaidOnly
    ? 'Resident Mark Not Paid ONLY'
    : 'Create Reimbursement Request'
  const subtitle = isNotPaidOnly
    ? 'This resident will be marked as not paid ONLY. A reimbursement request will not be opened at this time.'
    : [residentName, applicantId ? `Applicant ${applicantId}` : ''].filter(Boolean).join(' • ')

  const handleConfirmNotPaidOnly = () => {
    onSelect('NOT_NOW')
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex animate-fadeIn items-center justify-center bg-black/50 p-4"
      data-testid="start-reimbursement-process-overlay"
      data-variant={variant}
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl animate-slideUp overflow-y-auto rounded-xl bg-white shadow-2xl"
        data-testid="start-reimbursement-process-panel"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="start-reimbursement-process-title"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-blue-200 bg-blue-50 text-blue-600">
              <DollarSign size={22} aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <h2
                id="start-reimbursement-process-title"
                className="text-xl font-bold text-gray-900"
              >
                {title}
              </h2>
              {subtitle ? (
                <p
                  className={`mt-0.5 text-sm text-gray-600 ${isNotPaidOnly ? 'leading-snug' : 'truncate'}`}
                  data-testid="start-reimbursement-process-subtitle"
                >
                  {subtitle}
                </p>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 transition-colors hover:text-gray-600"
            aria-label="Close"
            data-testid="start-reimbursement-process-close-button"
          >
            <X size={22} aria-hidden="true" />
          </button>
        </div>

        <div className="space-y-5 p-6" data-testid="start-reimbursement-process-body">
          <ReimbursementTimelineSections />

          {isNotPaidOnly ? null : (
            <div className="animate-fadeIn">
              <div className="mb-4 flex items-start gap-3">
                <HelpCircle className="mt-0.5 shrink-0 text-blue-500" size={22} aria-hidden="true" />
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    Is the resident still in the unit?
                  </h3>
                  <p className="mt-1 text-sm text-gray-700">
                    Your answer determines the reimbursement type.
                  </p>
                </div>
              </div>
              <div className="space-y-3">
                {IN_UNIT_ACTIONS.map(({ value, label, description, icon: Icon, testId, choice }) => (
                  <button
                    key={value}
                    type="button"
                    data-testid={testId}
                    onClick={() => onSelect(choice)}
                    className="group flex w-full items-start gap-4 rounded-2xl border-2 border-gray-200 bg-white p-4 text-left transition-all hover:border-blue-300 hover:bg-blue-50/40 focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-100"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500 transition-colors group-hover:bg-blue-100 group-hover:text-blue-600">
                      <Icon size={21} aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-base font-semibold leading-tight text-gray-900">
                        {label}
                      </span>
                      <span className="mt-1 block text-sm leading-6 text-slate-700">
                        {description}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div
          className="flex flex-col-reverse gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-end sm:rounded-b-xl"
          data-testid="start-reimbursement-process-footer"
        >
          <button
            type="button"
            onClick={onClose}
            data-testid="start-reimbursement-process-cancel-button"
            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 sm:w-auto"
          >
            Cancel
          </button>
          {isNotPaidOnly ? (
            <button
              type="button"
              onClick={handleConfirmNotPaidOnly}
              data-testid="start-reimbursement-process-continue-button"
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 sm:w-auto"
            >
              Confirm
            </button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  )
}
